import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Typography, message, Table, DatePicker,
    Statistic, Progress, Space, Button, Divider, Tooltip, Tag,
    Empty, Dropdown, Menu, List, Checkbox, Badge
} from 'antd';
import {
    FileTextOutlined,
    DownloadOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    PercentageOutlined,
    CalendarOutlined,
    PieChartOutlined,
    BulbOutlined,
    CheckCircleOutlined,
    PrinterOutlined,
    ExportOutlined,
    InfoCircleOutlined,
    DashboardOutlined,
    HistoryOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { formatMoneyJOD, formatDateTime, formatDate } from '../utils/formatters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Reports = () => {
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const [data, setData] = useState({
        summary: {
            totalRevenue: 0,
            totalExpenses: 0,
            netProfit: 0,
            avgDailyExpense: 0
        },
        expenseBreakdown: [],
        timeline: []
    });

    const [checklist, setChecklist] = useState([
        { id: 1, text: 'All raw receipts digitized and recorded', done: false },
        { id: 2, text: 'Categorization accuracy confirmed', done: false },
        { id: 3, text: 'Large anomalies (>500 JOD) reviewed', done: false },
        { id: 4, text: 'Period CSV exported for archives', done: false },
        { id: 5, text: 'Dolibarr sales reconciliation complete', done: false }
    ]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const from = dates[0].format('YYYY-MM-DD');
            const to = dates[1].format('YYYY-MM-DD');
            const headers = { Authorization: `Bearer ${token}` };

            const res = await axios.get(`/api/accounting/reports/summary?startDate=${from}&endDate=${to}`, { headers });
            // Merge with safe defaults so any partial API response never crashes the UI
            setData(prev => ({
                summary: {
                    totalRevenue: 0,
                    totalExpenses: 0,
                    netProfit: 0,
                    avgDailyExpense: 0,
                    ...(res.data?.summary || {})
                },
                expenseBreakdown: Array.isArray(res.data?.expenseBreakdown) ? res.data.expenseBreakdown : [],
                timeline: Array.isArray(res.data?.timeline) ? res.data.timeline : []
            }));
        } catch (error) {
            message.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dates]);

    const handleExport = async (type = 'current') => {
        try {
            const token = localStorage.getItem('token');
            let from = dates[0].format('YYYY-MM-DD');
            let to = dates[1].format('YYYY-MM-DD');

            if (type === 'month') {
                from = dayjs().startOf('month').format('YYYY-MM-DD');
                to = dayjs().endOf('month').format('YYYY-MM-DD');
            } else if (type === 'last_month') {
                from = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
                to = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
            }

            const headers = { Authorization: `Bearer ${token}` };
            const response = await axios.get(`/api/accounting/reports/export?startDate=${from}&endDate=${to}`, {
                headers,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `MakerAI_Financial_${from}_to_${to}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success(`Report exported for period: ${from} to ${to}`);
        } catch (error) {
            message.error('Failed to export CSV');
        }
    };

    const breakdownColumns = [
        {
            title: 'Category',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <Badge color={record.color} />
                    <Text strong>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Transaction Count',
            dataIndex: 'count',
            key: 'count',
            align: 'center',
            width: 150
        },
        {
            title: 'Total Amount (JOD)',
            dataIndex: 'value',
            key: 'value',
            render: (val) => <Text strong>{formatMoneyJOD(val)}</Text>,
            align: 'right'
        },
        {
            title: 'Distribution',
            key: 'percent',
            render: (_, record) => {
                const total = safeSummary?.totalExpenses || 0;
                const percent = total > 0 ? ((record.value || 0) / total) * 100 : 0;
                return (
                    <div style={{ width: 140 }}>
                        <Progress
                            percent={parseFloat(percent.toFixed(1))}
                            size="small"
                            strokeColor={record.color}
                            format={(p) => `${p}%`}
                        />
                    </div>
                );
            },
            align: 'right'
        }
    ];

    const safeTimeline = Array.isArray(data.timeline) ? data.timeline : [];
    const safeBreakdown = Array.isArray(data.expenseBreakdown) ? data.expenseBreakdown : [];
    const safeSummary = data.summary || { totalRevenue: 0, totalExpenses: 0, netProfit: 0, avgDailyExpense: 0 };
    const maxTimelineValue = safeTimeline.length > 0
        ? Math.max(...safeTimeline.map(t => parseFloat(t.value) || 0), 1)
        : 1;
    const profitMargin = (safeSummary.totalRevenue || 0) > 0
        ? (((safeSummary.netProfit || 0) / (safeSummary.totalRevenue || 1)) * 100).toFixed(1)
        : 0;

    const exportMenuItems = [
        {
            key: 'current',
            label: `Selected Range (${dates[0].format('MMM DD')} - ${dates[1].format('MMM DD')})`,
            icon: <CalendarOutlined />
        },
        {
            key: 'month',
            label: 'This Calendar Month',
            icon: <HistoryOutlined />
        },
        {
            key: 'last_month',
            label: 'Last Calendar Month',
            icon: <HistoryOutlined />
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            {/* Action Bar Header */}
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Financial Analysis & Reporting</Title>
                    <Text type="secondary">Enterprise-grade financial insights and audit preparation</Text>
                </div>
                <Space size="middle">
                    <div style={{ background: '#fff', padding: '6px 12px', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                        <RangePicker
                            value={dates}
                            onChange={setDates}
                            allowClear={false}
                            bordered={false}
                        />
                    </div>
                    <Dropdown menu={{ items: exportMenuItems, onClick: ({ key }) => handleExport(key) }} placement="bottomRight">
                        <Button size="large" icon={<ExportOutlined />} loading={loading}>Export CSV</Button>
                    </Dropdown>
                    <Button size="large" type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>Print / Save PDF</Button>
                </Space>
            </div>

            {/* Content Grid */}
            <Row gutter={[24, 24]}>
                {/* Left Side: Analytics */}
                <Col xs={24} lg={16}>
                    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                        <Col span={6}>
                            <Card className="premium-card shadow-sm" bodyStyle={{ padding: '16px' }}>
                                <Statistic title={<Text type="secondary">TOTAL REVENUE</Text>} value={safeSummary.totalRevenue} formatter={val => formatMoneyJOD(val)} valueStyle={{ fontWeight: 800, color: '#3f8600' }} />
                                <div style={{ marginTop: 8 }}><Tag color="success" icon={<ArrowUpOutlined />}>Verified</Tag></div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card className="premium-card shadow-sm" bodyStyle={{ padding: '16px' }}>
                                <Statistic title={<Text type="secondary">TOTAL EXPENSES</Text>} value={safeSummary.totalExpenses} formatter={val => formatMoneyJOD(val)} valueStyle={{ fontWeight: 800, color: '#cf1322' }} />
                                <div style={{ marginTop: 8 }}><Tag color="error" icon={<ArrowDownOutlined />}>Recorded</Tag></div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card className="premium-card shadow-sm" bodyStyle={{ padding: '16px' }}>
                                <Statistic title={<Text type="secondary">NET SURPLUS</Text>} value={safeSummary.netProfit} formatter={val => formatMoneyJOD(val)} valueStyle={{ fontWeight: 800, color: (safeSummary.netProfit || 0) >= 0 ? '#1890ff' : '#cf1322' }} />
                                <div style={{ marginTop: 8 }}><Tag color={(safeSummary.netProfit || 0) >= 0 ? 'processing' : 'warning'}>{(safeSummary.netProfit || 0) >= 0 ? 'Healthy' : 'Deficit'}</Tag></div>
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card className="premium-card shadow-sm" bodyStyle={{ padding: '16px' }}>
                                <Statistic title={<Text type="secondary">PROFIT MARGIN</Text>} value={profitMargin} suffix="%" valueStyle={{ fontWeight: 800, color: '#722ed1' }} />
                                <div style={{ marginTop: 8 }}><Tag color="purple" icon={<PercentageOutlined />}>Efficiency</Tag></div>
                            </Card>
                        </Col>
                    </Row>

                    <Card
                        title={<Space><DashboardOutlined /> Aggregate Spending Trends</Space>}
                        className="premium-card shadow-sm"
                        style={{ marginBottom: 24 }}
                    >
                        {safeTimeline.length > 0 ? (
                            <div style={{ padding: '20px 0' }}>
                                <div style={{ height: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: '8px' }}>
                                    {safeTimeline.map((t, idx) => {
                                        const heightPercent = ((parseFloat(t.value) || 0) / maxTimelineValue) * 100;
                                        return (
                                            <Tooltip key={idx} title={
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '11px', opacity: 0.8 }}>{dayjs(t.label).format('MMM DD, YYYY')}</div>
                                                    <div style={{ fontWeight: 'bold' }}>{formatMoneyJOD(t.value)}</div>
                                                </div>
                                            }>
                                                <div style={{
                                                    flex: 1,
                                                    height: `${Math.max(4, heightPercent)}%`,
                                                    background: 'linear-gradient(180deg, #40a9ff 0%, #1890ff 100%)',
                                                    borderRadius: '4px 4px 0 0',
                                                    transition: 'all 0.3s'
                                                }} />
                                            </Tooltip>
                                        );
                                    })}
                                </div>
                                <div style={{ borderTop: '1px solid #f0f0f0', marginTop: '16px', paddingTop: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary" strong>{dates[0].format('MMM DD, YYYY')}</Text>
                                    <Text type="secondary" strong>DAILY BURN TIMELINE</Text>
                                    <Text type="secondary" strong>{dates[1].format('MMM DD, YYYY')}</Text>
                                </div>
                            </div>
                        ) : (
                            <Empty description="No data found for the selected period" />
                        )}
                    </Card>

                    <Card
                        title={<Space><PieChartOutlined /> Categorized Expenditure Analysis</Space>}
                        className="premium-card shadow-sm"
                        bodyStyle={{ padding: 0 }}
                    >
                        <Table
                            dataSource={safeBreakdown}
                            columns={breakdownColumns}
                            pagination={false}
                            rowKey="name"
                            size="large"
                            locale={{ emptyText: <Empty description="No categories recorded in this range" /> }}
                        />
                    </Card>
                </Col>

                {/* Right Side: Insights & Audit */}
                <Col xs={24} lg={8}>
                    <Card
                        title={<Space><BulbOutlined style={{ color: '#faad14' }} /> Smart Financial Insights</Space>}
                        className="premium-card shadow-sm"
                        style={{ marginBottom: 24 }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div>
                                <Text type="secondary" strong style={{ fontSize: '12px' }}>AVERAGE DAILY BURN</Text>
                                <div style={{ fontSize: '24px', fontWeight: 800, color: '#262626' }}>{formatMoneyJOD(safeSummary.avgDailyExpense)}</div>
                                <Text type="secondary" italic>Current operational spending rate</Text>
                            </div>
                            
                            <div>
                                <Text type="secondary" strong style={{ fontSize: '12px' }}>ESTIMATED MONTHLY COMMITMENT</Text>
                                <div style={{ fontSize: '24px', fontWeight: 800, color: '#262626' }}>{formatMoneyJOD((safeSummary.avgDailyExpense || 0) * 30)}</div>
                                <Text type="secondary" italic>Projected 30-day forecast</Text>
                            </div>

                            <Divider style={{ margin: '8px 0' }} />

                            <div>
                                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 8 }}>Budget Sustainability</Text>
                                <Row align="middle" gutter={12}>
                                    <Col span={18}>
                                        <Progress 
                                            percent={safeSummary.totalExpenses > 0 ? Math.min(100, Math.round((safeSummary.totalRevenue / safeSummary.totalExpenses) * 100)) : 100} 
                                            status={safeSummary.totalRevenue >= safeSummary.totalExpenses ? 'success' : 'exception'}
                                            strokeWidth={12}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Text strong>{safeSummary.totalExpenses > 0 ? Math.round((safeSummary.totalRevenue / safeSummary.totalExpenses) * 100) : 100}%</Text>
                                    </Col>
                                </Row>
                                <Text type="secondary" style={{ fontSize: '12px', marginTop: 4, display: 'block' }}>
                                    {safeSummary.totalRevenue >= safeSummary.totalExpenses 
                                        ? 'Revenue currently covers all operational costs.' 
                                        : 'Operational costs exceed current revenue intake.'}
                                </Text>
                            </div>
                        </div>
                    </Card>

                    <Card
                        title={<Space><CheckCircleOutlined style={{ color: '#52c41a' }} /> Period Audit Checklist</Space>}
                        className="premium-card shadow-sm"
                    >
                        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                            Professional audit steps for financial closing:
                        </Text>
                        <List
                            dataSource={checklist}
                            renderItem={item => (
                                <List.Item style={{ border: 'none', padding: '8px 0' }}>
                                    <Checkbox
                                        checked={item.done}
                                        onChange={(e) => {
                                            const newChecklist = checklist.map(i => i.id === item.id ? { ...i, done: e.target.checked } : i);
                                            setChecklist(newChecklist);
                                        }}
                                    >
                                        <Text delete={item.done} type={item.done ? 'secondary' : 'default'} style={{ fontSize: '14px' }}>{item.text}</Text>
                                    </Checkbox>
                                </List.Item>
                            )}
                        />
                        <Divider style={{ margin: '16px 0' }} />
                        <Button
                            block
                            type="primary"
                            size="large"
                            disabled={!checklist.every(i => i.done)}
                            icon={<CheckCircleOutlined />}
                        >
                            Seal Period Records
                        </Button>
                    </Card>

                    <Card size="small" style={{ marginTop: 24, background: '#f0f5ff', border: '1px solid #adc6ff' }}>
                        <Space align="start">
                            <InfoCircleOutlined style={{ color: '#1890ff', marginTop: 4 }} />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                This report is synchronized with local expense logs and Dolibarr revenue records. Ensure all manual entries are verified before exporting for official use.
                            </Text>
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Reports;
